import {useEffect, useState} from "react"
import {useUser} from "@/contexts/UserContextProvider"
import AuthError from "@/components/auth/AuthError"
import {useNavigate, useLocation} from "react-router-dom"
import {useAuthUserData} from "@/utils/hooks/useOrgUsers"
import {useOrgIntegrationRequirements} from "@/utils/hooks/useOrgUsers"
import {UserDashboardData} from "@/types/database.types"

const AuthenticatedRoute = ({children}: {children: React.ReactNode}) => {
  const {session} = useUser()
  const navigate = useNavigate()
  const location = useLocation()
  const [isAuthorized, setIsAuthorized] = useState<boolean>(false)
  const [isAdmin, setIsAdmin] = useState<boolean>(false)
  const [isOnboarded, setIsOnboarded] = useState<boolean>(false)

  // Load organisation-level integration requirements
  const {
    requirements: orgRequirements,
    loading: reqLoading,
    error: reqError,
  } = useOrgIntegrationRequirements()

  const {data, loading, error} = useAuthUserData(session?.user?.id)

  console.info("🔍 [Auth] Data:", data)

  function setAllStatesToFalse() {
    setIsAuthorized(false)
    setIsAdmin(false)
    setIsOnboarded(false)
  }

  useEffect(() => {
    console.info("🔍 [Auth] State:", {
      hasSession: !!session,
      loading,
      hasError: !!error,
    })

    if (loading || error || !session || !data) {
      if (error) {
        console.info("❌ [Auth] Load failed:", error)
      }
      setAllStatesToFalse()
      return
    }

    console.info("✅ [Auth] Load successful")
    setIsAuthorized(true)

    if (data?.role === "ADMIN") {
      setIsAdmin(true)
      console.info("✅ [Auth] Admin access granted")
    }

    /*
     * Our onboarding logic needs to consider three dimensions:
     *  1. Per-admin whitelist (exempt from integrations)
     *  2. Org-level integration requirements (all required must be connected)
     *  3. Fallback: at least one integration connected.
     */

    const computeOnboardingState = () => {
      if (!data) return false

      const integrations: Record<string, any> =
        (data.integrations as Record<string, any>) ?? {}

      // Gather connected providers – if integrations object uses provider keys, include those keys
      const connectedProviders = Object.entries(integrations)
        .filter(([_, integration]) => integration?.status === "connected")
        .map(([provider, integration]) => provider || integration?.provider)

      // User-level integration requirements (includes is_fulfilled flags)
      const integrationRequirements: Record<string, any> =
        (data.integration_requirements as Record<string, any>) ?? {}

      const hasRequirements = Object.keys(integrationRequirements).length > 0

      const allUserReqsFulfilled = hasRequirements
        ? Object.values(integrationRequirements).every(
            (req: any) => req?.is_fulfilled === true
          )
        : true

      // NOTE: `is_whitelisted` indicates admins *exempt* from integration checks.
      // Default to `false` so that a missing flag is treated as non-whitelisted.
      const isWhitelisted = (data as UserDashboardData)?.is_whitelisted ?? false

      // 1a. Not whitelisted admin → always onboarded.
      if (data.role === "ADMIN" && !isWhitelisted) {
        return true
      }

      // 1. Admin exempt from integrations
      if (data.role === "ADMIN" && isWhitelisted === false) {
        return true
      }

      // 2a. Per-user requirements present → all must be fulfilled
      if (hasRequirements) {
        return allUserReqsFulfilled
      }

      // 2b. Org-level requirements present → *all* must be connected
      if ((orgRequirements?.length ?? 0) > 0) {
        const requiredProviders = (orgRequirements ?? []).map(
          (req) => req.provider
        )
        const allMet = requiredProviders.every((p) =>
          connectedProviders.includes(p)
        )
        return allMet
      }

      // 3. Fallback → any integration connected
      return connectedProviders.length > 0
    }

    const onboardingComplete = computeOnboardingState()

    console.info("🔍 [Auth] Onboarding Check:", {
      isWhitelisted: (data as any)?.is_whitelisted,
      integrationRequirements: data?.integration_requirements,
      orgRequirements,
      integrations: data?.integrations,
      onboardingResult: onboardingComplete,
    })

    setIsOnboarded(onboardingComplete)
  }, [session, data, loading, error, orgRequirements, reqLoading])

  // If the user is an admin and currently on the neutral loading route,
  // automatically forward them to the default portal home.
  useEffect(() => {
    if (
      isAuthorized &&
      isAdmin &&
      isOnboarded &&
      location.pathname === "/loading"
    ) {
      navigate("/archive", {replace: true})
    }
  }, [isAuthorized, isAdmin, isOnboarded, location.pathname, navigate])

  // Allow non-admin users to access certain public-safe routes (e.g., loading page)
  const NON_ADMIN_ALLOWED_ROUTES = ["/loading"]

  if (loading || reqLoading || isAuthorized === null) {
    return (
      <div className="flex h-screen flex-col items-center justify-center">
        Loading...
      </div>
    )
  }

  if (!isAuthorized) {
    return <AuthError type="not_logged_in" pageContainer={true} />
  }

  if (!isOnboarded) {
    navigate("/auth/onboarding/configure_integrations")
    return null
  }

  if (!isAdmin && !NON_ADMIN_ALLOWED_ROUTES.includes(location.pathname)) {
    return <AuthError type="not_admin" pageContainer={true} />
  }

  return children
}

export default AuthenticatedRoute
